import { storeToRefs } from 'pinia';
import type { ComputedRef, EffectScope, Ref } from 'vue';
import { computed, effectScope, onScopeDispose, ref, shallowRef, watch } from 'vue';
import type { IMpResolvedCombination, IPopularMixParlay } from '@/interface/IPopularMixParlay';
import { MarketPage } from '@/interface/enum';
import { useMpTicketState } from '@/libraries/recommendation/useMpTicketState';
import { useRootStore } from '@/stores/rootStore';

interface CombinationEntry {
    scope: EffectScope;
    state: ComputedRef<IMpResolvedCombination>;
    hasResolved: Ref<boolean>;
}

export function useResolvedMpCombinations(recommendations: Ref<IPopularMixParlay[]>) {
    const { marketPage } = storeToRefs(useRootStore());
    const pollInterval = computed(() => (marketPage.value === MarketPage.Live ? 30000 : 60000));
    const entriesByKey = new Map<string, CombinationEntry>();
    const activeEntries = shallowRef<CombinationEntry[]>([]);

    onScopeDispose(() => {
        entriesByKey.forEach(entry => entry.scope.stop());
        entriesByKey.clear();
        activeEntries.value = [];
    });

    function createEntry(combination: IPopularMixParlay): CombinationEntry {
        const scope = effectScope(true);
        try {
            const state = scope.run(() => {
                const tickets = combination.ticketKeys.map(ticketKey => useMpTicketState(ticketKey, pollInterval));
                const resolved = computed<IMpResolvedCombination>(() => ({
                    key: combination.key,
                    tickets: tickets.map(ticket => ticket.ticket.value),
                    isResolved: tickets.every(ticket => ticket.isResolved.value),
                    isValid: tickets.length > 0 && tickets.every(ticket => ticket.isValid.value),
                }));
                const hasResolved = ref(false);
                watch(() => resolved.value.isResolved, (isResolved) => {
                    if (isResolved) hasResolved.value = true;
                }, { immediate: true });

                return { state: resolved, hasResolved };
            })!;
            return { scope, ...state };
        } catch (error) {
            scope.stop();
            throw error;
        }
    }

    watch(recommendations, (combinations) => {
        const activeKeys = new Set(combinations.map(combination => combination.key));
        entriesByKey.forEach((entry, key) => {
            if (!activeKeys.has(key)) {
                entry.scope.stop();
                entriesByKey.delete(key);
            }
        });

        activeEntries.value = combinations.map((combination) => {
            let entry = entriesByKey.get(combination.key);
            if (!entry) {
                entry = createEntry(combination);
                entriesByKey.set(combination.key, entry);
            }
            return entry;
        });
    }, { immediate: true });

    const resolvedCombinations = computed(() => activeEntries.value.map(entry => entry.state.value));
    const visibleCombinations = computed(() => resolvedCombinations.value.filter(combination => combination.isValid));
    const hasCompletedInitialResolution = ref(false);
    watch(() => activeEntries.value.length > 0 && activeEntries.value.every(entry => entry.hasResolved.value), (hasResolved) => {
        if (hasResolved) hasCompletedInitialResolution.value = true;
    }, { immediate: true });

    return { resolvedCombinations, visibleCombinations, hasCompletedInitialResolution };
}
